// =============================================================================
// Agenda Utils - Intervalli di date civili per la vista agenda
// =============================================================================
// Tutta l'aritmetica sta su date civili ("yyyy-MM-dd") del tenant: sommare
// giorni a un istante sballerebbe di un'ora ai cambi di ora legale.
// =============================================================================

import type { Locale } from 'date-fns';
import { format } from 'date-fns';
import {
  addCivilDays,
  civilDayOfWeek,
  civilDayToCarrier,
  type CivilDate,
} from '../../../lib/tenantTime';
import type { BoundedViewMode } from '../../../store/schedulerStore';

/** Intervallo di giorni civili con estremi INCLUSIVI. */
export interface CivilRange {
  start: CivilDate;
  end: CivilDate;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Ultimo giorno del mese di una data civile (calcolato in UTC, mai in locale). */
function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Intervallo coperto dall'agenda per una data di riferimento.
 * La settimana e' lunedi'-domenica, il mese e' quello solare.
 */
export function civilRangeFor(anchor: CivilDate, mode: BoundedViewMode): CivilRange {
  if (mode === 'day') {
    return { start: anchor, end: anchor };
  }

  if (mode === 'week') {
    // civilDayOfWeek: 0 = domenica → offset dal lunedi'
    const offsetFromMonday = (civilDayOfWeek(anchor) + 6) % 7;
    const start = addCivilDays(anchor, -offsetFromMonday);
    return { start, end: addCivilDays(start, 6) };
  }

  const [year, month] = anchor.split('-').map(Number);
  return {
    start: `${year}-${pad(month)}-01`,
    end: `${year}-${pad(month)}-${pad(lastDayOfMonth(year, month))}`,
  };
}

/** Sposta la data di riferimento di un intero periodo avanti (+1) o indietro (-1). */
export function shiftCivilAnchor(anchor: CivilDate, mode: BoundedViewMode, direction: 1 | -1): CivilDate {
  if (mode === 'day') return addCivilDays(anchor, direction);
  if (mode === 'week') return addCivilDays(anchor, 7 * direction);

  const [year, month] = anchor.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1 + direction, 1));
  return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-01`;
}

/**
 * Etichetta dell'intervallo per la barra di navigazione.
 * I carrier (mezzanotte locale) servono solo a dare in pasto la data civile ai
 * formattatori di date-fns: non sono istanti e non escono di qui.
 */
export function formatCivilRangeLabel(range: CivilRange, mode: BoundedViewMode, locale: Locale): string {
  const startCarrier = civilDayToCarrier(range.start);
  const endCarrier = civilDayToCarrier(range.end);

  if (mode === 'day') {
    return format(startCarrier, 'EEEE d MMMM yyyy', { locale });
  }

  if (mode === 'month') {
    return format(startCarrier, 'MMMM yyyy', { locale });
  }

  const sameMonth = range.start.slice(0, 7) === range.end.slice(0, 7);
  const startPattern = sameMonth ? 'd' : 'd MMM';
  return `${format(startCarrier, startPattern, { locale })} – ${format(endCarrier, 'd MMM yyyy', { locale })}`;
}
